"use client"

import * as React from "react"

import { OperationsLastClosedTermSection } from "./operations/operations-last-closed-term-section"
import { OperationsTermSection } from "./operations/operations-term-section"
import type { DashboardDemoMode } from "@/lib/evaluation-dashboard-context"
import {
  surveysOnboardingShowsCurrentTermOnly,
  type SurveysOnboardingOperationsStage,
} from "@/lib/evaluation-dashboard-onboarding"
import { buildOperationsTermSections } from "@/lib/mock/evaluation-operations"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

interface EvaluationDashboardOperationsViewProps {
  currentTerm?: EvaluationTerm
  demo?: DashboardDemoMode
  /** Limits what appears while first-term setup is still in progress. */
  reveal?: SurveysOnboardingOperationsStage
}

export function EvaluationDashboardOperationsView({
  currentTerm,
  demo = null,
  reveal = "full",
}: EvaluationDashboardOperationsViewProps) {
  const currentOnly = surveysOnboardingShowsCurrentTermOnly(reveal)
  const compact = reveal === "guided"
  const showLifecycle = reveal === "collecting" || reveal === "full"

  const sections = React.useMemo(() => {
    const built = buildOperationsTermSections(demo)
    if (currentOnly) {
      return built.filter(section => section.term.timelinePosition === "current")
    }
    return built
  }, [currentOnly, demo])

  return (
    <div className="flex flex-col gap-10">
      {sections.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          No active survey windows across your terms.
        </div>
      ) : (
        <div id="eval-operations-terms" className="flex flex-col gap-8">
          {sections.map(section =>
            section.term.timelinePosition === "last" ? (
              <OperationsLastClosedTermSection key={section.term.id} term={section.term} />
            ) : (
              <OperationsTermSection
                key={section.term.id}
                term={section.term}
                rows={section.rows}
                showLifecycle={
                  showLifecycle && currentTerm != null && section.term.id === currentTerm.id
                }
                compact={compact}
              />
            ),
          )}
        </div>
      )}
    </div>
  )
}
